import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import RoketAvatar from "../components/RoketAvatar";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Send,
  Loader2,
  Bot,
  User,
  Container,
  MapPin,
  Ship,
  Calendar,
  Sparkles,
  Headphones,
  AlertCircle,
  X,
  Phone,
  Mail,
  MessageSquare,
  UserCheck,
} from "lucide-react";


export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [supportRequestSent, setSupportRequestSent] = useState(false);
  const [sendingSupport, setSendingSupport] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportForm, setSupportForm] = useState({
    email: "",
    phone: "",
    description: "",
  });
  const [staffJoined, setStaffJoined] = useState(false);
  const [staffName, setStaffName] = useState(null);
  const [supportRequestId, setSupportRequestId] = useState(null);
  const { getAuthHeaders, user, token } = useAuth();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const wsRef = useRef(null);

  // Pre-fill email when user data is available
  useEffect(() => {
    if (user?.email) {
      setSupportForm(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // WebSocket connection for live support
  useEffect(() => {
    if (!supportRequestId || !token) return;

    const wsUrl = `${API_BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/live-support/ws/customer/${supportRequestId}?token=${token}`;
    
    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log("Customer WebSocket connected");
        };
        
        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          
          if (data.type === "staff_joined") {
            setStaffJoined(true);
            setStaffName(data.staff_name);
            toast.success(`${data.staff_name} has joined the chat!`);
            if (data.message) {
              setMessages(prev => [...prev, {
                role: "system",
                content: data.message.content
              }]);
            }
          } else if (data.type === "new_message") {
            setMessages(prev => [...prev, {
              role: data.message.role,
              content: data.message.content,
              sender_name: data.message.sender_name,
              timestamp: data.message.timestamp
            }]);
          } else if (data.type === "session_resolved") {
            toast.info("This support session has been resolved.");
            if (data.message) {
              setMessages(prev => [...prev, {
                role: "system",
                content: data.message.content
              }]);
            }
          }
        };
        
        ws.onclose = () => {
          console.log("Customer WebSocket disconnected");
          // Only reconnect if we still have a support request
          if (supportRequestId) {
            setTimeout(connectWebSocket, 3000);
          }
        };
        
        ws.onerror = (error) => {
          console.error("WebSocket error:", error);
        };
        
        wsRef.current = ws;
      } catch (error) {
        console.error("WebSocket connection error:", error);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [supportRequestId, token]);

  const handleOpenSupportModal = () => {
    if (supportRequestSent) return;
    setShowSupportModal(true);
  };

  const handleSubmitSupportRequest = async (e) => {
    e.preventDefault();
    if (sendingSupport || supportRequestSent) return;
    
    // Validate form
    if (!supportForm.email || !supportForm.phone) {
      toast.error("Please provide your email and phone number.");
      return;
    }
    
    setSendingSupport(true);
    try {
      const response = await axios.post(
        `${API_URL}/chat/support-request`,
        {
          session_id: sessionId,
          email: supportForm.email,
          phone: supportForm.phone,
          description: supportForm.description || "User requested human assistance",
          chat_history: messages.map(m => ({
            role: m.role,
            content: m.content,
            tracking_data: m.tracking_data || null
          }))
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setSupportRequestSent(true);
        setSupportRequestId(response.data.request_id); // Store request ID for WebSocket
        setShowSupportModal(false);
        toast.success("Support request sent! Our team will contact you shortly.");
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `I've notified our support team about your request. A staff member will reach out to you at ${supportForm.email} or ${supportForm.phone} shortly. Is there anything else I can help you with in the meantime?`,
          },
        ]);
      }
    } catch (error) {
      toast.error("Failed to send support request. Please try again.");
    } finally {
      setSendingSupport(false);
    }
  };

  useEffect(() => {
    // Add welcome message from Roket
    setMessages([
      {
        role: "assistant",
        content: `Hey ${user?.full_name || "there"}! I'm Roket, your friendly tracking assistant! 🌊\n\nMy name comes from Rok + Europacific Tracking, and I'm here to help you navigate the shipping world!\n\nYou can:\n- Ask me to track a container (e.g., "Track MAEU1234567")\n- Ask about shipping status meanings\n- Get information about ETAs and vessel schedules\n\nWhat can I help you with today?`,
      },
    ]);
  }, [user]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/chat/message`,
        {
          message: userMessage,
          session_id: sessionId,
        },
        { headers: getAuthHeaders() }
      );

      setSessionId(response.data.session_id);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: response.data.response,
          tracking_data: response.data.tracking_data,
        },
      ]);
    } catch (error) {
      toast.error("Failed to get response. Please try again.");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const suggestedQuestions = [
    "Track MAEU1234567",
    "What does 'In Transit' mean?",
    "How long does shipping take from Shanghai to Rotterdam?",
    "Track MSCU9876543",
  ];

  return (
    <div
      className="bg-background flex flex-col"
      style={{ minHeight: 'calc(100vh - var(--tasks-panel-height, 0px))' }}
    >
      <Navbar />

      <main className="flex-1 pt-16 flex flex-col">
        <div className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 flex flex-col">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="py-6 border-b border-border/30"
          >
            <div className="flex items-center gap-3">
              <motion.div 
                className="border-2 border-primary/30 shadow-md rounded-full"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <RoketAvatar size={48} animate={true} />
              </motion.div>
              <div>
                <h1 className="font-heading text-xl font-bold text-foreground">
                  Chat with Roket
                </h1>
                <p className="text-sm text-muted-foreground">
                  Your AI Shipping Assistant • Powered by GPT-5.2
                </p>
              </div>
            </div>
          </motion.div>

          {/* Messages Area */}
          <ScrollArea className="flex-1 py-6">
            <div className="space-y-6">
              <AnimatePresence mode="popLayout">
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`flex gap-4 ${
                      message.role === "user" 
                        ? "justify-end" 
                        : message.role === "system"
                        ? "justify-center"
                        : "justify-start"
                    }`}
                  >
                    {/* System messages */}
                    {message.role === "system" ? (
                      <div className="px-4 py-2 bg-secondary/50 rounded-full text-xs text-muted-foreground flex items-center gap-2">
                        <UserCheck className="h-3 w-3" />
                        {message.content}
                      </div>
                    ) : (
                      <>
                        {/* AI Assistant avatar */}
                        {message.role === "assistant" && (
                          <div className="flex-shrink-0">
                            <RoketAvatar size={32} animate={true} />
                          </div>
                        )}
                        
                        {/* Staff avatar */}
                        {message.role === "staff" && (
                          <div className="h-8 w-8 rounded-full bg-green-500 flex-shrink-0 flex items-center justify-center">
                            <Headphones className="h-4 w-4 text-white" strokeWidth={1.5} />
                          </div>
                        )}

                        <div
                          className={`max-w-[80%] ${
                            message.role === "user"
                              ? "bg-primary text-primary-foreground rounded-tl-xl rounded-tr-sm rounded-br-xl rounded-bl-xl"
                              : message.role === "staff"
                              ? "bg-green-500/10 border border-green-500/30 rounded-tl-sm rounded-tr-xl rounded-br-xl rounded-bl-xl"
                              : "bg-card border border-border/50 rounded-tl-sm rounded-tr-xl rounded-br-xl rounded-bl-xl"
                          } p-4`}
                        >
                          {/* Staff name label */}
                          {message.role === "staff" && message.sender_name && (
                            <div className="text-xs text-green-600 font-medium mb-1 flex items-center gap-1">
                              <UserCheck className="h-3 w-3" />
                              {message.sender_name}
                            </div>
                          )}
                          
                          <div className="whitespace-pre-wrap text-sm leading-relaxed">
                            {message.content}
                          </div>

                          {/* Tracking Data Card */}
                          {message.tracking_data && (
                            <div className="mt-4 p-4 bg-background/50 rounded border border-border/30">
                              <div className="flex items-center gap-2 mb-3">
                                <Container className="h-4 w-4 text-primary" strokeWidth={1.5} />
                                <span className="font-code text-sm font-bold text-foreground">
                                  {message.tracking_data.container_number}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  ({message.tracking_data.carrier})
                                </span>
                              </div>

                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">Location:</span>
                                  <span className="text-foreground">{message.tracking_data.location}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Ship className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">Vessel:</span>
                                  <span className="text-foreground">
                                    {message.tracking_data.vessel_name || "N/A"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">ETA:</span>
                                  <span className="text-foreground">
                                    {message.tracking_data.eta || "N/A"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`h-2 w-2 rounded-full ${
                                    message.tracking_data.status?.toLowerCase().includes("transit")
                                      ? "bg-accent"
                                      : "bg-success"
                                  }`} />
                                  <span className="text-foreground">{message.tracking_data.status}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {message.role === "user" && (
                          <div className="h-8 w-8 rounded bg-secondary flex-shrink-0 flex items-center justify-center">
                            <User className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                          </div>
                        )}
                      </>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Loading indicator */}
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-4"
                >
                  <div className="h-8 w-8 rounded bg-primary/20 flex-shrink-0 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary" strokeWidth={1.5} />
                  </div>
                  <div className="bg-card border border-border/50 rounded-xl p-4">
                    <div className="loading-dots flex gap-1">
                      <span className="h-2 w-2 rounded-full bg-primary" />
                      <span className="h-2 w-2 rounded-full bg-primary" />
                      <span className="h-2 w-2 rounded-full bg-primary" />
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Suggested Questions */}
          {messages.length <= 1 && (
            <div className="py-4 border-t border-border/30">
              <p className="text-sm text-muted-foreground mb-3">Suggested questions:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => setInput(question)}
                    className="text-sm px-3 py-1.5 bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground rounded transition-colors"
                    data-testid={`suggested-question-${index}`}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="py-4 border-t border-border/30">
            <form onSubmit={handleSubmit} className="flex gap-3">
              <Input
                ref={inputRef}
                type="text"
                placeholder="Ask about tracking, shipping status, or enter a container number..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="input-industrial flex-1"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                className="btn-industrial px-6"
                disabled={loading || !input.trim()}
                data-testid="chat-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </Button>
            </form>
            
            {/* Talk to Staff Button and Disclaimer */}
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenSupportModal}
                disabled={supportRequestSent}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                data-testid="talk-to-staff-btn"
              >
                <Headphones className="h-4 w-4" />
                {supportRequestSent ? "Request Sent" : "Talk to Staff"}
              </Button>
              
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70">
                <AlertCircle className="h-3 w-3 flex-shrink-0" />
                <span className="italic">
                  AI chatbot may produce inaccurate information about people, places, or facts.
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Support Request Modal */}
      <Dialog open={showSupportModal} onOpenChange={setShowSupportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Headphones className="h-5 w-5 text-primary" />
              Talk to Our Team
            </DialogTitle>
            <DialogDescription>
              Fill in your contact details and we'll get back to you as soon as possible.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmitSupportRequest} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="support-email" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Email Address *
              </Label>
              <Input
                id="support-email"
                type="email"
                placeholder="your@email.com"
                value={supportForm.email}
                onChange={(e) => setSupportForm(prev => ({ ...prev, email: e.target.value }))}
                required
                data-testid="support-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="support-phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                Phone Number *
              </Label>
              <Input
                id="support-phone"
                type="tel"
                placeholder="+386 40 123 456"
                value={supportForm.phone}
                onChange={(e) => setSupportForm(prev => ({ ...prev, phone: e.target.value }))}
                required
                data-testid="support-phone-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="support-description" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                How can we help?
              </Label>
              <Textarea
                id="support-description"
                placeholder="Describe what you need help with..."
                value={supportForm.description}
                onChange={(e) => setSupportForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="support-description-input"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowSupportModal(false)}
                disabled={sendingSupport}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sendingSupport || !supportForm.email || !supportForm.phone}
                data-testid="submit-support-request-btn"
              >
                {sendingSupport ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  "Send Request"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { ChatPage };
